import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import IconButton from "@mui/material/IconButton";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import RefreshIcon from "@mui/icons-material/Refresh";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "../i18n/LocaleContext";
import { LocalizedMessage, renderMessage } from "../i18n/LocaleContext";
import {
  AuthorizedFetch,
  TenantAdminError,
  TenantAdminPatch,
  TenantAdminSummary,
  TenantAdminUser,
  TenantDirectoryUser,
  createTenantAdminUser,
  deleteTenantAdminUser,
  fetchTenantAdminSummary,
  fetchTenantAdminUsers,
  fetchTenantDirectory,
  patchTenantAdminUser,
} from "../utils/tenantAdminApi";

interface TenantAdminPanelProps {
  authorizedFetch: AuthorizedFetch;
  /** oid do usuário autenticado: a linha dele não tem exclusão nem alternância de admin. */
  currentUserId: string;
  className?: string;
}

/** Campo em gravação. Um PATCH por usuário de cada vez, como no console da uTech. */
type Saving = { appUserId: string; field: keyof TenantAdminPatch } | null;

function errorMessage(error: unknown): LocalizedMessage {
  if (error instanceof TenantAdminError) return error.message$;
  return { key: "tenantAdmin.error.generic" };
}

/**
 * Ramal editável na linha. Valor local enquanto editado, gravado no blur ou no Enter, descartado
 * no Esc — o mesmo comportamento da tabela do msteams-ui, de onde esta tela é espelhada.
 */
const ExtensionField: React.FC<{
  value: string;
  disabled: boolean;
  onCommit: (next: string) => Promise<boolean>;
  /** Marca o campo como inválido: a recusa do backend foi sobre o ramal desta linha. */
  error: boolean;
  /** Chamado à primeira tecla e no Esc: é o que apaga a mensagem da tentativa anterior. */
  onDismissError: () => void;
}> = ({ value, disabled, onCommit, error, onDismissError }) => {
  const [draft, setDraft] = useState(value);
  // Esc dispara blur, e o blur acontece antes de o React re-renderizar com o rascunho já
  // descartado — sem este ref o commit do blur leria o valor digitado e **salvaria** o que o Esc
  // deveria ter jogado fora.
  const discardRef = useRef(false);

  // Realinha com o valor salvo quando ele muda (gravação bem-sucedida, refresh ou recarga).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = async () => {
    // Esc: descarta a edição e, com ela, a mensagem de erro — o usuário desistiu da alteração.
    if (discardRef.current) {
      discardRef.current = false;
      setDraft(value);
      onDismissError();
      return;
    }
    const next = draft.trim();
    // Igual ao salvo: nada a enviar. Realinha o rascunho para descartar só os espaços.
    if (next === value) {
      setDraft(value);
      return;
    }
    // Recusado (ramal em uso, por exemplo): o rascunho **permanece**. Voltar ao valor salvo
    // deixava a tela contraditória — o ramal anterior, que não tem conflito nenhum, aparecia
    // acompanhado da mensagem de conflito, como se o errado fosse ele. Mantendo o valor
    // digitado, a mensagem se refere ao que está à vista, e o campo marcado como inválido liga
    // os dois. A saída é corrigir (a mensagem sai à primeira tecla) ou Esc.
    await onCommit(next);
  };

  return (
    <TextField
      size="small"
      value={draft}
      disabled={disabled}
      error={error}
      onChange={(event) => {
        setDraft(event.target.value);
        onDismissError();
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
        if (event.key === "Escape") {
          // O Esc aqui só desiste da edição. Sem isto ele borbulha até o listener da `window` no
          // TenantAdminScreen, que também o trata como "voltar" e tiraria o usuário da tela.
          event.stopPropagation();
          discardRef.current = true;
          (event.target as HTMLInputElement).blur();
        }
      }}
      className="tenant-admin-extension"
      slotProps={{ input: { inputMode: "numeric" } }}
    />
  );
};

const TenantAdminPanel: React.FC<TenantAdminPanelProps> = ({ authorizedFetch, currentUserId, className }) => {
  const { t } = useTranslation();

  const [summary, setSummary] = useState<TenantAdminSummary | null>(null);
  const [users, setUsers] = useState<TenantAdminUser[] | null>(null);
  const [directoryIds, setDirectoryIds] = useState<Set<string> | null>(null);
  const [loadError, setLoadError] = useState<LocalizedMessage | null>(null);
  // `field` identifica o controle recusado, para marcá-lo como inválido junto com a mensagem —
  // sem isso a mensagem fica solta na linha, sem dizer a que controle se refere. Opcional porque
  // uma falha de exclusão é da linha inteira, não de um campo.
  const [rowError, setRowError] = useState<
    { appUserId: string; field?: keyof TenantAdminPatch; message: LocalizedMessage } | null
  >(null);
  const [saving, setSaving] = useState<Saving>(null);
  const [filter, setFilter] = useState("");
  // Os dois filtros se combinam (E): marcados juntos, listam os administradores licenciados.
  const [onlyLicensed, setOnlyLicensed] = useState(false);
  const [onlyAdmins, setOnlyAdmins] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TenantAdminUser | null>(null);

  // Resumo, usuários e diretório em paralelo. O diretório serve só para marcar quem saiu da
  // organização: uma falha nele não pode esconder a lista, então ele tem tratamento próprio.
  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;

    setLoadError(null);
    Promise.all([
      fetchTenantAdminSummary(authorizedFetch, controller.signal),
      fetchTenantAdminUsers(authorizedFetch, controller.signal),
    ])
      .then(([loadedSummary, loadedUsers]) => {
        if (disposed) return;
        setSummary(loadedSummary);
        setUsers(loadedUsers);
      })
      .catch((error) => {
        if (disposed || controller.signal.aborted) return;
        console.warn(`[tenantAdmin] falha ao carregar: ${error instanceof Error ? error.message : String(error)}`);
        setLoadError(error instanceof TenantAdminError ? error.message$ : { key: "tenantAdmin.error.loadFailed" });
      });

    fetchTenantDirectory(authorizedFetch, controller.signal)
      .then((directory) => {
        if (!disposed) setDirectoryIds(new Set(directory.map((user) => user.id)));
      })
      .catch(() => {
        // Sem o diretório a coluna de "saiu da organização" simplesmente não aparece.
        if (!disposed) setDirectoryIds(null);
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [authorizedFetch, refreshKey]);

  const licensedCount = useMemo(
    () => (users ?? []).filter((user) => user.licenced).length,
    [users]
  );
  const maxLicenses = summary?.license ?? 0;
  const licencesExhausted = licensedCount >= maxLicenses;

  const visibleUsers = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (users ?? [])
      .filter((user) => (onlyLicensed ? user.licenced : true))
      .filter((user) => (onlyAdmins ? user.permissions.tenantAdmin : true))
      .filter((user) => {
        if (!term) return true;
        return (
          user.tenantUserData.displayName.toLowerCase().includes(term)
          || user.tenantUserData.mail.toLowerCase().includes(term)
          || user.extension.includes(term)
        );
      })
      .sort((a, b) =>
        a.tenantUserData.displayName.localeCompare(b.tenantUserData.displayName, undefined, { numeric: true })
      );
  }, [users, filter, onlyLicensed, onlyAdmins]);

  // Aplica um PATCH e substitui o usuário na lista.
  //
  // Um salvamento por usuário de cada vez: duas respostas concorrentes sobre o mesmo documento
  // chegariam em ordem indefinida, e cada uma traz o usuário inteiro como o servidor o tinha
  // naquele instante — a que chegasse por último poderia reverter a outra na tela.
  //
  // O guard fica aqui, e NÃO em `disabled` nos controles vizinhos: desabilitá-los os fazia piscar
  // (o MUI pinta o switch de cinza e volta) a cada alteração de qualquer um deles. É a mesma razão
  // pela qual o msteams-ui mantém o guard no handler. O guard é por usuário, não global: linhas
  // diferentes são documentos independentes e podem ser salvas em paralelo.
  const applyPatch = useCallback(
    async (user: TenantAdminUser, field: keyof TenantAdminPatch, changes: TenantAdminPatch): Promise<boolean> => {
      if (saving?.appUserId === user.id) return false;
      setSaving({ appUserId: user.id, field });
      setRowError(null);
      try {
        const updated = await patchTenantAdminUser(authorizedFetch, user.id, changes);
        setUsers((current) => (current ?? []).map((item) => (item.id === updated.id ? updated : item)));
        return true;
      } catch (error) {
        setRowError({ appUserId: user.id, field, message: errorMessage(error) });
        return false;
      } finally {
        setSaving(null);
      }
    },
    [authorizedFetch, saving]
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteTenantAdminUser(authorizedFetch, pendingDelete.id);
      setUsers((current) => (current ?? []).filter((item) => item.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (error) {
      setRowError({ appUserId: pendingDelete.id, message: errorMessage(error) });
      setPendingDelete(null);
    }
  }, [authorizedFetch, pendingDelete]);

  const renderRow = (user: TenantAdminUser) => {
    const isSelf = user.tenantUserData.id === currentUserId;
    const inTenant = directoryIds ? directoryIds.has(user.tenantUserData.id) : true;
    // Só o controle que está sendo salvo é desabilitado — os vizinhos ficam intactos (ver o
    // comentário do applyPatch sobre o piscar).
    const isSaving = (field: keyof TenantAdminPatch) => saving?.appUserId === user.id && saving.field === field;
    const licenceLocked = !user.licenced && licencesExhausted;

    const permissionSwitch = (
      field: "recordAccess" | "pabxContactsAccess" | "tenantAdmin",
      checked: boolean,
      label: string,
      disabled = false
    ) => (
      <Switch
        size="small"
        checked={checked}
        disabled={disabled || isSaving(field)}
        onChange={() => applyPatch(user, field, { [field]: !checked })}
        slotProps={{ input: { "aria-label": `${label} — ${user.tenantUserData.displayName}` } }}
      />
    );

    const cell = (label: string, control: React.ReactNode) => (
      <div className="tenant-admin-cell" data-label={label}>
        <span className="tenant-admin-cell-label">{label}</span>
        {control}
      </div>
    );

    return (
      <article key={user.id} className="tenant-admin-row" data-self={isSelf ? "true" : "false"}>
        <div className="tenant-admin-identity" data-in-tenant={inTenant ? "true" : "false"}>
          <strong>
            {user.tenantUserData.displayName || user.tenantUserData.id}
            {isSelf && <span className="tenant-admin-self"> ({t("tenantAdmin.self")})</span>}
          </strong>
          <span>{user.tenantUserData.mail}</span>
          {!inTenant && <span className="tenant-admin-warning">{t("tenantAdmin.notInTenant")}</span>}
        </div>

        {cell(
          t("tenantAdmin.column.extension"),
          <ExtensionField
            value={user.extension}
            disabled={isSaving("extension")}
            onCommit={(next) => applyPatch(user, "extension", { extension: next })}
            error={rowError?.appUserId === user.id && rowError.field === "extension"}
            // A mensagem se refere à tentativa anterior: some à primeira tecla da correção. Sem
            // isto ela ficaria presa quando a correção não gera gravação nenhuma — voltar ao ramal
            // original, ou limpar o campo que já estava vazio.
            onDismissError={() => setRowError((current) => (current?.appUserId === user.id ? null : current))}
          />
        )}

        {cell(
          t("tenantAdmin.column.licence"),
          licenceLocked ? (
            <Tooltip title={t("tenantAdmin.licenceLimit")} enterTouchDelay={0}>
              {/* span: o Tooltip não dispara sobre elemento desabilitado. */}
              <span>
                <Switch size="small" checked={false} disabled slotProps={{ input: { "aria-label": t("tenantAdmin.column.licence") } }} />
              </span>
            </Tooltip>
          ) : (
            <Switch
              size="small"
              checked={user.licenced}
              disabled={isSaving("licenced")}
              onChange={() => applyPatch(user, "licenced", { licenced: !user.licenced })}
              slotProps={{ input: { "aria-label": `${t("tenantAdmin.column.licence")} — ${user.tenantUserData.displayName}` } }}
            />
          )
        )}

        {cell(
          t("tenantAdmin.column.recordAccess"),
          permissionSwitch("recordAccess", user.permissions.recordAccess, t("tenantAdmin.column.recordAccess"))
        )}

        {cell(
          t("tenantAdmin.column.pabxContacts"),
          permissionSwitch("pabxContactsAccess", user.permissions.pabxContactsAccess, t("tenantAdmin.column.pabxContacts"))
        )}

        {/* O próprio administrador não se desmarca — senão trancaria a si mesmo fora desta tela.
            O backend recusa igualmente (409 reason: self); aqui o switch já nasce desabilitado. */}
        {cell(
          t("tenantAdmin.column.admin"),
          isSelf ? (
            <Tooltip title={t("tenantAdmin.selfAdminLocked")} enterTouchDelay={0}>
              <span>
                <Switch size="small" checked disabled slotProps={{ input: { "aria-label": t("tenantAdmin.column.admin") } }} />
              </span>
            </Tooltip>
          ) : (
            permissionSwitch("tenantAdmin", user.permissions.tenantAdmin, t("tenantAdmin.column.admin"))
          )
        )}

        <div className="tenant-admin-actions">
          {!isSelf && (
            <Tooltip title={t("tenantAdmin.delete.tooltip")} enterTouchDelay={0}>
              {/* Sem `disabled` durante um salvamento: o ícone piscaria junto, e abrir o diálogo
                  de confirmação não altera nada por si. */}
              <IconButton
                size="small"
                aria-label={`${t("tenantAdmin.delete.tooltip")} — ${user.tenantUserData.displayName}`}
                onClick={() => setPendingDelete(user)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {rowError?.appUserId === user.id && (
          <Alert severity="error" className="tenant-admin-row-error">
            {renderMessage(t, rowError.message)}
          </Alert>
        )}
      </article>
    );
  };

  return (
    <section className={`status-card tenant-admin-card${className ? ` ${className}` : ""}`}>
      <div className="card-header tenant-admin-header">
        <div>
          <h2>{t("tenantAdmin.title")}</h2>
          <p className="tenant-admin-subtitle">
            {summary?.clientName}
            {summary && ` · ${t("tenantAdmin.licenses", { used: licensedCount, total: maxLicenses })}`}
          </p>
        </div>
        <div className="tenant-admin-toolbar">
          <TextField
            size="small"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t("tenantAdmin.search")}
            className="tenant-admin-search"
          />
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAddAlt1Icon />}
            onClick={() => setAddOpen(true)}
          >
            {t("tenantAdmin.add")}
          </Button>
          <IconButton size="small" aria-label={t("tenantAdmin.refresh")} onClick={() => setRefreshKey((key) => key + 1)}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="tenant-admin-filters">
        <label className="tenant-admin-filter">
          <Checkbox
            size="small"
            checked={onlyLicensed}
            onChange={(event) => setOnlyLicensed(event.target.checked)}
          />
          {t("tenantAdmin.filter.licensed")}
        </label>
        <label className="tenant-admin-filter">
          <Checkbox
            size="small"
            checked={onlyAdmins}
            onChange={(event) => setOnlyAdmins(event.target.checked)}
          />
          {t("tenantAdmin.filter.admins")}
        </label>
      </div>

      {loadError && <Alert severity="error">{renderMessage(t, loadError)}</Alert>}

      {!users && !loadError && (
        <p className="call-search-placeholder">
          <CircularProgress size={16} /> {t("tenantAdmin.loading")}
        </p>
      )}

      {users && visibleUsers.length === 0 && (
        <p className="call-search-placeholder">{t("tenantAdmin.empty")}</p>
      )}

      {users && visibleUsers.length > 0 && (
        <div className="tenant-admin-list" aria-live="polite">
          <div className="tenant-admin-columns" aria-hidden="true">
            <span>{t("tenantAdmin.column.user")}</span>
            <span>{t("tenantAdmin.column.extension")}</span>
            {/* O contador acompanha o cabeçalho da coluna, e não só o subtítulo: com a lista
                rolada é aqui que se olha, e é o que explica os switches de licença desabilitados
                quando o limite do cliente foi atingido. */}
            <span>
              {t("tenantAdmin.column.licence")}{" "}
              <span className="tenant-admin-licence-count" data-exhausted={licencesExhausted ? "true" : "false"}>
                {licensedCount}/{maxLicenses}
              </span>
            </span>
            <span>{t("tenantAdmin.column.recordAccess")}</span>
            <span>{t("tenantAdmin.column.pabxContacts")}</span>
            <span>{t("tenantAdmin.column.admin")}</span>
            <span>{t("tenantAdmin.column.actions")}</span>
          </div>
          {visibleUsers.map(renderRow)}
        </div>
      )}

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>{t("tenantAdmin.delete.title")}</DialogTitle>
        <DialogContent>
          {pendingDelete && (pendingDelete.extension
            ? t("tenantAdmin.delete.confirm", {
                name: pendingDelete.tenantUserData.displayName,
                extension: pendingDelete.extension,
              })
            : t("tenantAdmin.delete.confirmNoExtension", { name: pendingDelete.tenantUserData.displayName }))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>{t("tenantAdmin.cancel")}</Button>
          <Button color="error" onClick={confirmDelete}>{t("tenantAdmin.delete.action")}</Button>
        </DialogActions>
      </Dialog>

      {addOpen && (
        <AddTenantUsersDialog
          authorizedFetch={authorizedFetch}
          registeredIds={new Set((users ?? []).map((user) => user.tenantUserData.id))}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            setAddOpen(false);
            setRefreshKey((key) => key + 1);
          }}
        />
      )}
    </section>
  );
};

interface AddTenantUsersDialogProps {
  authorizedFetch: AuthorizedFetch;
  registeredIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}

/**
 * Cadastro a partir do diretório da organização. Só o oid de cada escolhido é enviado: o nome e o
 * e-mail gravados são os que o backend busca no Graph.
 */
const AddTenantUsersDialog: React.FC<AddTenantUsersDialogProps> = ({
  authorizedFetch,
  registeredIds,
  onClose,
  onAdded,
}) => {
  const { t } = useTranslation();
  const [directory, setDirectory] = useState<TenantDirectoryUser[] | null>(null);
  const [error, setError] = useState<LocalizedMessage | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;

    fetchTenantDirectory(authorizedFetch, controller.signal)
      .then((users) => {
        if (!disposed) setDirectory(users);
      })
      .catch((requestError) => {
        if (disposed || controller.signal.aborted) return;
        setError(requestError instanceof TenantAdminError ? requestError.message$ : { key: "tenantAdmin.error.loadFailed" });
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [authorizedFetch]);

  const available = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (directory ?? [])
      .filter((user) => !registeredIds.has(user.id))
      .filter((user) => {
        if (!term) return true;
        return (
          (user.displayName ?? "").toLowerCase().includes(term)
          || (user.mail ?? "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", undefined, { numeric: true }));
  }, [directory, registeredIds, filter]);

  // Cadastro sequencial, acumulando as falhas: as que deram certo já estão gravadas, então a lista
  // é recarregada de todo modo.
  const submit = async () => {
    setAdding(true);
    setError(null);
    let failures = 0;
    for (const userId of Array.from(selected)) {
      try {
        await createTenantAdminUser(authorizedFetch, userId);
      } catch {
        failures += 1;
      }
    }
    setAdding(false);
    if (failures > 0) {
      setError({ key: "tenantAdmin.add.failed", params: { count: failures } });
      return;
    }
    onAdded();
  };

  const toggle = (userId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} className="tenant-admin-add-dialog">
      <DialogTitle>{t("tenantAdmin.add.title")}</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t("tenantAdmin.add.search")}
          sx={{ marginBottom: 1 }}
        />
        {error && <Alert severity="error">{renderMessage(t, error)}</Alert>}
        {!directory && !error && (
          <p className="call-search-placeholder">
            <CircularProgress size={16} /> {t("tenantAdmin.add.loading")}
          </p>
        )}
        {directory && available.length === 0 && (
          <p className="call-search-placeholder">{t("tenantAdmin.add.empty")}</p>
        )}
        {available.map((user) => (
          <label key={user.id} className="tenant-admin-add-option">
            <Checkbox size="small" checked={selected.has(user.id)} onChange={() => toggle(user.id)} />
            <span>
              <strong>{user.displayName || user.id}</strong>
              <span>{user.mail}</span>
            </span>
          </label>
        ))}
      </DialogContent>
      <DialogActions>
        <span className="tenant-admin-add-count">
          {t("tenantAdmin.add.selected", { count: selected.size })}
        </span>
        <Button onClick={onClose}>{t("tenantAdmin.cancel")}</Button>
        <Button variant="contained" disabled={selected.size === 0 || adding} onClick={submit}>
          {t("tenantAdmin.add.action")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantAdminPanel;
